import { useEffect, useState } from 'react'
import { msg, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import CloseIcon from '@mui/icons-material/Close'
import ImageIcon from '@mui/icons-material/Image'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import OptionIconPickerDialog from './option-icon-picker-dialog'
import { ConfigOptionIcon } from '../shared/config-option-icon'
import {
  DEFAULT_OPTION_ICON_FILENAME,
  normalizeStoredOptionIcon,
  resolveDefaultOptionIconFilename,
} from '../../utils/config-option-icons'

function buildOptionsIconsDraft(options = []) {
  return options.map((option) => ({
    ...option,
    icon: option.icon ?? '',
  }))
}

/**
 * Dialog per assegnare icone mappa alle option di un campo select (admin).
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {string} props.canonicalLabel
 * @param {Array<object>} [props.options]
 * @param {(payload: { options: Array<object> }) => void} props.onSave
 */
export default function ConfigOptionIconsDialog({
  open,
  onClose,
  canonicalLabel,
  options = [],
  onSave,
}) {
  const theme = useTheme()
  const { _ } = useLingui()
  const [optionsDraft, setOptionsDraft] = useState([])
  const [iconPickerIndex, setIconPickerIndex] = useState(null)
  const titleColor = theme.palette.newColors?.gray || theme.palette.text.primary
  const canSave = Boolean(String(canonicalLabel ?? '').trim()) && optionsDraft.length > 0

  useEffect(() => {
    if (!open) return
    setOptionsDraft(buildOptionsIconsDraft(options))
    setIconPickerIndex(null)
  }, [open, options])

  const handleSave = () => {
    const updatedOptions = options.map((option, index) => ({
      ...option,
      icon: normalizeStoredOptionIcon(
        optionsDraft[index]?.icon
          || resolveDefaultOptionIconFilename(canonicalLabel, option),
      ),
    }))
    onSave({ options: updatedOptions })
    onClose()
  }

  const canonicalBox = (
    <Box
      sx={{
        mb: 2.5,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor:
          theme.palette.mode === 'light'
            ? theme.palette.grey[50]
            : theme.palette.action.hover,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: theme.palette.text.secondary,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 0.5,
        }}
      >
        <Trans>Canonical label (data key)</Trans>
      </Typography>
      <Typography
        component="code"
        sx={{
          fontFamily: 'var(--font-mono, monospace)',
          fontSize: '0.9rem',
          fontWeight: 600,
          color: titleColor,
          wordBreak: 'break-word',
        }}
      >
        {canonicalLabel || '—'}
      </Typography>
    </Box>
  )

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: titleColor, fontWeight: 700, pr: 6 }}>
          <Trans>Option icons</Trans>
          <IconButton
            aria-label={_(msg`Close`)}
            onClick={onClose}
            sx={{ position: 'absolute', right: 12, top: 12, color: titleColor }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            <Trans>
              Assign a map icon to each option. Icons are stored on the server (data/config-icons) and served via the API.
            </Trans>
          </Typography>
          {canonicalBox}
          <Stack spacing={2}>
            {optionsDraft.map((option, optionIndex) => {
              const effectiveIcon = resolveDefaultOptionIconFilename(canonicalLabel, option)
              return (
                <Box
                  key={`icon-${option.value}-${option.label}-${optionIndex}`}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: `1px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      flexShrink: 0,
                    }}
                  >
                    {effectiveIcon ? (
                      <ConfigOptionIcon
                        filename={effectiveIcon}
                        size={32}
                        color={theme.palette.text.secondary}
                        alt={option.label}
                      />
                    ) : (
                      <ImageIcon sx={{ color: theme.palette.text.disabled, fontSize: 28 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                      {effectiveIcon || _(msg`No icon selected`)}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setIconPickerIndex(optionIndex)}
                  >
                    <Trans>Choose icon</Trans>
                  </Button>
                </Box>
              )
            })}
          </Stack>
          {optionsDraft.length === 0 && (
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              <Trans>No options available for this select field.</Trans>
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={onClose}
            sx={{ color: theme.palette.newColors?.gray || theme.palette.text.secondary }}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={{
              background: theme.palette.newColors?.cyanGradient,
              color: theme.palette.newColors?.lightGray ?? 'white',
              fontWeight: 600,
              '&:hover': {
                background: theme.palette.newColors?.cyanGradient,
                opacity: 0.9,
              },
              '&.Mui-disabled': {
                opacity: 0.5,
              },
            }}
          >
            <Trans>Save</Trans>
          </Button>
        </DialogActions>
      </Dialog>

      <OptionIconPickerDialog
        open={iconPickerIndex != null}
        onClose={() => setIconPickerIndex(null)}
        value={iconPickerIndex != null ? optionsDraft[iconPickerIndex]?.icon : null}
        optionLabel={iconPickerIndex != null ? optionsDraft[iconPickerIndex]?.label : ''}
        onSelect={(filename) => {
          if (iconPickerIndex == null) return
          setOptionsDraft((prev) =>
            prev.map((option, index) =>
              index === iconPickerIndex
                ? { ...option, icon: normalizeStoredOptionIcon(filename) }
                : option
            )
          )
        }}
        onIconRenamed={(fromFilename, toFilename) => {
          setOptionsDraft((prev) =>
            prev.map((option) =>
              option.icon === fromFilename
                ? { ...option, icon: toFilename }
                : option
            )
          )
        }}
        onIconDeleted={(filename) => {
          setOptionsDraft((prev) =>
            prev.map((option) =>
              option.icon === filename
                ? { ...option, icon: DEFAULT_OPTION_ICON_FILENAME }
                : option
            )
          )
        }}
      />
    </>
  )
}
